import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import apiClient from '../services/api';
import {
  clearSilentSsoFailedFlag,
  consumeSsoReturnTo,
  isSilentSsoInteractionError,
  markSilentSsoFailedThisVisit,
} from '../utils/silentSso';

/**
 * Callback OIDC sur le host frontend (`/auth/callback`).
 * Le code est échangé par l'API ; aucun jeton n'est écrit dans localStorage.
 */
const AuthCallbackPage: React.FC = () => {
  const [message, setMessage] = useState('Finalisation de la connexion…');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    const code = params.get('code');
    const state = params.get('state');

    if (error) {
      markSilentSsoFailedThisVisit();
      if (isSilentSsoInteractionError(error)) {
        window.location.replace('/login?silent_sso=failed');
        return;
      }
      window.location.replace(
        `/login?silent_sso=failed&oauth_error=${encodeURIComponent(error)}`,
      );
      return;
    }

    if (!code || !state) {
      markSilentSsoFailedThisVisit();
      window.location.replace('/login?silent_sso=failed&oauth_error=no_code');
      return;
    }

    void (async () => {
      try {
        const response = await apiClient.post('/auth/sso/callback', { code, state });
        const body = response.data ?? {};
        if (body.status === 'select_organization') {
          window.location.replace('/auth/select-organization');
          return;
        }
        if (body.status === 'forbidden') {
          markSilentSsoFailedThisVisit();
          const codeName = encodeURIComponent(body.code || 'ORG_NOT_SUBSCRIBED');
          window.location.replace(`/auth/access-denied?code=${codeName}`);
          return;
        }
        clearSilentSsoFailedFlag();
        const returnTo =
          (typeof body.returnTo === 'string' && body.returnTo) ||
          consumeSsoReturnTo('/');
        window.location.replace(returnTo);
      } catch (err: any) {
        markSilentSsoFailedThisVisit();
        const status = err?.response?.status;
        const payload = err?.response?.data;
        const accessCode = payload?.code || payload?.message?.code;
        if (status === 403) {
          const codeName = encodeURIComponent(accessCode || 'ORG_NOT_SUBSCRIBED');
          window.location.replace(`/auth/access-denied?code=${codeName}`);
          return;
        }
        setMessage('Connexion impossible. Retour à l’écran de connexion…');
        const detail =
          typeof payload?.message === 'string'
            ? payload.message
            : 'oauth_failed';
        window.setTimeout(() => {
          window.location.replace(
            `/login?silent_sso=failed&oauth_error=${encodeURIComponent(detail)}`,
          );
        }, 800);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
      <Loader2 className="w-10 h-10 text-indigo-600 animate-spin mb-4" />
      <p className="text-gray-700">{message}</p>
    </div>
  );
};

export default AuthCallbackPage;
